/**
 * registries.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Central registries for ResumeBar template SDK platform.
 * Exposes ManifestRegistry, CollectionRegistry, and PreviewRegistry classes.
 */

import { validateTemplate } from './validator';

class ManifestRegistryImpl {
  constructor() {
    this.manifests = new Map();
  }

  register(manifest) {
    if (!manifest || !manifest.id) {
      console.error('[SDK ManifestRegistry] Cannot register invalid manifest:', manifest);
      return false;
    }

    const { valid } = validateTemplate(manifest);
    if (!valid) {
      console.warn(`[SDK ManifestRegistry] Manifest "${manifest.id}" registered with validation errors.`);
    }

    this.manifests.set(manifest.id, manifest);
    return true;
  }

  get(id) {
    return this.manifests.get(id);
  }

  getAll() {
    return Array.from(this.manifests.values());
  }

  clear() {
    this.manifests.clear();
  }
}

class CollectionRegistryImpl {
  constructor() {
    this.collections = new Map();
  }

  register(collection) {
    if (!collection || !collection.id) {
      console.error('[SDK CollectionRegistry] Cannot register invalid collection:', collection);
      return false;
    }
    this.collections.set(collection.id, {
      id: collection.id,
      name: collection.name || collection.label || 'Collection',
      icon: collection.icon || '📄',
      description: collection.description || '',
      order: collection.order || 99,
      color: collection.color || '#7C3AED',
      templates: Array.isArray(collection.templates) ? collection.templates : []
    });
    return true;
  }

  get(id) {
    return this.collections.get(id);
  }

  getAll() {
    return Array.from(this.collections.values()).sort((a, b) => a.order - b.order);
  }

  clear() {
    this.collections.clear();
  }
}

class PreviewRegistryImpl {
  constructor() {
    this.previews = new Map();
  }

  register(templateId, previewAssets) {
    if (!templateId) return;
    this.previews.set(templateId, {
      card: previewAssets?.card || null,
      preview: previewAssets?.preview || null,
      full: previewAssets?.full || null
    });
  }

  getPreviews(templateId) {
    const assets = this.previews.get(templateId);
    return {
      card: assets?.card || null,
      preview: assets?.preview || null,
      full: assets?.full || null,
      hasStaticThumbnail: !!(assets?.card || assets?.preview)
    };
  }

  clear() {
    this.previews.clear();
  }
}

// Singletons instance exports for the app
export const ManifestRegistry = new ManifestRegistryImpl();
export const CollectionRegistry = new CollectionRegistryImpl();
export const PreviewRegistry = new PreviewRegistryImpl();
